// InternetNetworkingFundamentalsDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored (InterviewGamePlanDetail UI style)
// Updated: Example sections now use the same running example: “Facebook-like Home Feed”.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        /* ignore */
      }
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* ignore */
    }
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <h3 className="h6 text-uppercase text-muted mb-0">Sponsored</h3>
          <span className="badge rounded-pill text-bg-light border text-muted">Ads</span>
        </div>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small helpers (InterviewGamePlanDetail UI) ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <h2 className="h4 mb-2 fw-bold">{title}</h2>
    </div>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

const Say = ({ children }) => (
  <div className="alert alert-primary d-flex align-items-start gap-2 py-2 mb-3" role="note">
    <span className="badge text-bg-primary">Say</span>
    <div className="mb-0">{children}</div>
  </div>
);

const TagPill = ({ children }) => (
  <span className="badge rounded-pill text-bg-light border text-muted fw-semibold">{children}</span>
);

const Panel = ({ title, tag, children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
        <div className="fw-semibold">{title}</div>
        {tag ? <TagPill>{tag}</TagPill> : null}
      </div>
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="mb-0">
    {items.map((it, idx) => (
      <li key={idx} className={idx === items.length - 1 ? "" : "mb-3"}>
        <div>{it.main}</div>
        {it.detail ? <div className="medium text-muted mt-1">{it.detail}</div> : null}
      </li>
    ))}
  </ul>
);

const MonoBlock = ({ children }) => (
  <pre className="bg-light border rounded-3 p-3 mb-0" style={{ whiteSpace: "pre-wrap" }}>
    <code>{children}</code>
  </pre>
);

/* ================== TOC ================== */
const TOC = [
  { id: "dns-anycast-cdn-http", label: "A) DNS, Anycast, CDN, HTTP/1.1–3" },
  { id: "tls-mtls-alb", label: "B) TLS, mTLS, ALB/NLB/Ingress" },
  { id: "load-balancing", label: "C) L4/L7 LB, algos, health" },
  { id: "nat-vpc-egress", label: "D) NAT, VPC, private subnets, egress" },
  { id: "end-to-end", label: "E) End-to-end interview answer" },
  { id: "pitfalls", label: "F) Common pitfalls & quick fixes" },
];

/* ================== page ================== */
export default function InternetNetworkingFundamentalsDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // Running example context: Facebook-like Home Feed (same vibe as InterviewGamePlanDetail)
  const fb = useMemo(() => {
    const MAU = 500_000_000;
    const DAU = 200_000_000;
    const peakMultiplier = 6;
    const feedOpensPerUserPerDay = 8;
    const avgFeedRps = (DAU * feedOpensPerUserPerDay) / 86400;
    const peakFeedRps = avgFeedRps * peakMultiplier;
    return { MAU, DAU, peakMultiplier, feedOpensPerUserPerDay, avgFeedRps, peakFeedRps };
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Internet &amp; networking fundamentals</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(13,110,253,.08), rgba(25,135,84,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1 fw-bold">3) Internet &amp; networking fundamentals — detailed guide</h1>
          <p className="text-muted mb-0" style={{ maxWidth: 900, margin: "0 auto" }}>
            A reusable networking template for system design interviews, plus a running example:{" "}
            <strong>Facebook-like Home Feed</strong> (personalized feed + media via CDN).
          </p>
        </div>
      </header>

      {/* content: LEFT TOC + MAIN + RIGHT Sponsored */}
      <div className="row g-4">
        {/* LEFT: On this page */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <h3 className="h6 text-uppercase text-muted mb-0">On this page</h3>
                  <TagPill>TOC</TagPill>
                </div>

                <nav className="nav flex-column small">
                  {TOC.map(({ id: tocId, label }) => (
                    <a
                      key={tocId}
                      href={`#${tocId}`}
                      className={`nav-link px-0 ${activeId === tocId ? "fw-semibold text-primary" : ""}`}
                      aria-current={activeId === tocId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A */}
          <Section id="dns-anycast-cdn-http" title="A) DNS, Anycast, CDN, caching layers, HTTP/1.1 vs HTTP/2 vs HTTP/3">
            <Say>
              “I’ll explain the request path from name resolution to the edge, then the caching layers and HTTP versions
              that affect latency and tail behavior.”
            </Say>

            <Panel title="DNS — what to say & what it controls" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Flow:</strong> client → recursive resolver → authoritative nameserver → answer.
                      </>
                    ),
                    detail:
                      "Recursors cache answers by TTL. Authoritative servers control routing policies (geo/weighted) and record types.",
                  },
                  {
                    main: (
                      <>
                        <strong>Records:</strong> A/AAAA, CNAME, TXT, MX, SRV, CAA.
                      </>
                    ),
                    detail:
                      "In interviews, name a couple (A/AAAA/CNAME) and mention TXT (verification) + CAA (cert constraints).",
                  },
                  {
                    main: (
                      <>
                        <strong>TTL trade-off:</strong> low TTL helps steering/failover; high TTL improves stability.
                      </>
                    ),
                    detail:
                      "You can use low TTL during incidents or migrations, and a moderate TTL during steady-state.",
                  },
                  {
                    main: (
                      <>
                        <strong>Gotchas:</strong> apex CNAME limitations; split-horizon DNS (internal vs external).
                      </>
                    ),
                    detail:
                      "Use ALIAS/ANAME/flattening at apex. Use internal zones for service discovery and private endpoints.",
                  },
                ]}
              />
            </Panel>

            <Panel title="CDN & caching layers — where bytes and latency disappear" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Layers:</strong> browser → CDN edge → reverse proxy → Redis → DB buffer cache.
                      </>
                    ),
                    detail:
                      "Know which layer is responsible for which content: media/static at CDN; personalized APIs often bypass CDN but still benefit from edge routing.",
                  },
                  {
                    main: (
                      <>
                        <strong>Cache keys:</strong> path + query + <em>Vary</em> headers (Accept-Encoding; sometimes cookies/auth).
                      </>
                    ),
                    detail:
                      "Incorrect keys can leak user-specific content. Always call out Vary and auth boundaries.",
                  },
                  {
                    main: (
                      <>
                        <strong>Policies:</strong> TTL, stale-while-revalidate, origin shielding, signed URLs.
                      </>
                    ),
                    detail:
                      "Origin shield reduces thundering herds. SWR smooths p95 by serving stale while refreshing.",
                  },
                  {
                    main: (
                      <>
                        <strong>Invalidation:</strong> purge on write; versioned assets for immutable caching.
                      </>
                    ),
                    detail:
                      "Prefer versioned static assets (content-hash filenames) so you avoid frequent purges.",
                  },
                ]}
              />
            </Panel>

            <Panel title="HTTP versions — what changes for performance" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>HTTP/1.1:</strong> limited multiplexing; keep-alive helps; TCP HOL affects concurrency.
                      </>
                    ),
                    detail:
                      "Still common, but chatty pages benefit from HTTP/2/3.",
                  },
                  {
                    main: (
                      <>
                        <strong>HTTP/2:</strong> multiplex streams on one TCP connection, header compression; great for many small requests.
                      </>
                    ),
                    detail:
                      "Also foundational for gRPC.",
                  },
                  {
                    main: (
                      <>
                        <strong>HTTP/3 (QUIC):</strong> multiplex without TCP HOL; better on mobile; faster resume.
                      </>
                    ),
                    detail:
                      "Great for flaky networks and mobility; mention measuring impact rather than assuming.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — DNS/CDN/HTTP in practice" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>DNS steering:</strong> <code>www.facebook.com</code> resolves to an Anycast CDN/WAF VIP; moderate TTL (minutes) for stability.
                      </>
                    ),
                    detail:
                      "During incidents/migrations, TTL can be reduced temporarily to accelerate steering/failover.",
                  },
                  {
                    main: (
                      <>
                        <strong>Anycast edge:</strong> users hit a nearby POP for low latency and resilience.
                      </>
                    ),
                    detail:
                      "POP also absorbs DDoS and reduces cross-region latency for first byte.",
                  },
                  {
                    main: (
                      <>
                        <strong>CDN caching:</strong> images/videos are served from CDN with signed URLs; feed API returns metadata + media URLs.
                      </>
                    ),
                    detail:
                      "This pushes the heavy bytes (media) to the edge while keeping personalized feed reads fast and small.",
                  },
                  {
                    main: (
                      <>
                        <strong>HTTP/2/3 at the edge:</strong> multiplexing helps feed scroll (many small requests) and improves mobile experience.
                      </>
                    ),
                    detail:
                      "Net result: better p95 for repeated feed refresh + faster image loads on unstable networks.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* B */}
          <Section id="tls-mtls-alb" title="B) TLS termination, mutual TLS, ALB vs NLB, Kubernetes Ingress">
            <Say>“I’ll explain where TLS terminates, when we use mTLS, and why we pick L7 vs L4 load balancers.”</Say>

            <Panel title="TLS termination patterns" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Edge termination:</strong> TLS ends at CDN/WAF/LB; re-encrypt to origin.
                      </>
                    ),
                    detail:
                      "Centralizes cert management and enables L7 features (WAF, routing). Re-encrypt inside for defense-in-depth.",
                  },
                  {
                    main: (
                      <>
                        <strong>Passthrough:</strong> TLS terminates at the service (rare; compliance/special protocols).
                      </>
                    ),
                    detail:
                      "Less L7 visibility at the LB. Requires strong service-level observability and rotation automation.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Mutual TLS (mTLS) — when and why" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>What:</strong> both sides present certificates (cryptographic identity).
                      </>
                    ),
                    detail:
                      "Common with service mesh/SPIFFE. Still pair with app-layer authZ.",
                  },
                  {
                    main: (
                      <>
                        <strong>Where:</strong> service-to-service inside clusters; B2B APIs that require client certs.
                      </>
                    ),
                    detail:
                      "Mention rotation and SAN/issuer validation as first-class requirements.",
                  },
                ]}
              />
            </Panel>

            <Panel title="ALB (L7) vs NLB (L4) vs Kubernetes Ingress" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>ALB / L7 proxy:</strong> host/path routing, WAF, richer health, HTTP retries, auth offload, gRPC/websockets.
                      </>
                    ),
                    detail:
                      "Best for HTTP APIs and gradual rollouts (canary by headers).",
                  },
                  {
                    main: (
                      <>
                        <strong>NLB / L4:</strong> raw TCP/UDP, preserves source IP, low overhead.
                      </>
                    ),
                    detail:
                      "Use for custom protocols, high throughput TCP, or when you want minimal proxy behavior.",
                  },
                  {
                    main: (
                      <>
                        <strong>K8s Ingress/Gateway:</strong> controller implements L7 rules and routes to services.
                      </>
                    ),
                    detail:
                      "Ingress is north-south; service mesh can handle east-west with mTLS, retries, circuit breakers.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — TLS + LB choices" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>TLS termination:</strong> TLS terminates at CDN/WAF; origin traffic is re-encrypted to the regional ingress/L7 LB.
                      </>
                    ),
                    detail:
                      "Centralized cert management at the edge + secure hop to origin prevents plaintext inside networks.",
                  },
                  {
                    main: (
                      <>
                        <strong>L7 routing:</strong> edge forwards to L7 (ALB/Ingress) for host/path routing: <code>/api/feed</code>, <code>/api/posts</code>.
                      </>
                    ),
                    detail:
                      "L7 also supports canary routing (headers/percent) and consistent health checks.",
                  },
                  {
                    main: (
                      <>
                        <strong>mTLS internally:</strong> service-to-service calls (FeedReadSvc → PostSvc → UserSvc) use mTLS identity.
                      </>
                    ),
                    detail:
                      "Rotation is automated (mesh/SPIFFE). AuthZ still happens at each service boundary.",
                  },
                  {
                    main: (
                      <>
                        <strong>L4 where needed:</strong> for high-throughput TCP (if any) or specialized streaming paths, an L4 LB can be used.
                      </>
                    ),
                    detail:
                      "But for most feed APIs, L7 is the default due to routing and observability needs.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* In-article ad (optional) */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90, textAlign: "center" }} />
            </div>
          )}

          {/* C */}
          <Section id="load-balancing" title="C) Load balancing: L4 vs L7, algorithms, health checks, connection handling">
            <Say>
              “I’ll describe the load balancer layer, name a couple algorithms, and explain health checks, draining, and
              outlier ejection.”
            </Say>

            <Panel title="Algorithms to name (and when to use them)" tag="Template">
              <BulletList
                items={[
                  { main: <><strong>Round Robin / Weighted RR</strong></>, detail: "Simple distribution; good default for homogeneous fleets." },
                  { main: <><strong>Least Connections / Least Pending</strong></>, detail: "Better when requests have variable duration." },
                  { main: <><strong>EWMA / Least Time</strong></>, detail: "Route around slow hosts; helps p95/p99." },
                  { main: <><strong>Consistent Hashing</strong></>, detail: "Sticky by key (userId/session) for cache locality." },
                  { main: <><strong>Power of Two Choices</strong></>, detail: "Scalable load-aware routing with low overhead." },
                ]}
              />
            </Panel>

            <Panel title="Health checks & safe deployments" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Active health:</strong> readiness vs liveness; dependency-aware checks.
                      </>
                    ),
                    detail:
                      "Readiness gates traffic during deploys; liveness restarts dead processes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Passive health:</strong> outlier ejection on 5xx/latency spikes.
                      </>
                    ),
                    detail:
                      "Prevents one bad host from poisoning p95/p99.",
                  },
                  {
                    main: (
                      <>
                        <strong>Connection lifecycle:</strong> slow-start for new pods, draining for old pods, tuned keep-alives.
                      </>
                    ),
                    detail:
                      "Avoids cold-start spikes and reduces dropped requests on rollouts.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — LB behavior under peak" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Traffic:</strong> feed reads are read-heavy and tail-sensitive; peak can be very high (e.g., ~
                        {Math.round(fb.peakFeedRps).toLocaleString()} RPS at ×{fb.peakMultiplier} multiplier).
                      </>
                    ),
                    detail:
                      "This is why you care about outlier ejection and queue control at the LB and service layers.",
                  },
                  {
                    main: (
                      <>
                        <strong>Routing:</strong> L7 routes <code>/feed</code> to FeedRead fleet; <code>/posts</code> to PostWrite fleet; rate limits are stricter on writes.
                      </>
                    ),
                    detail:
                      "Separating read/write paths simplifies scaling and protects the read SLO during spikes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Algorithm:</strong> EWMA/least-time for stateless feed APIs; consistent hashing when you want cache affinity (e.g., session → warm cache).
                      </>
                    ),
                    detail:
                      "Avoid client-IP stickiness because many users sit behind NAT or mobile carrier gateways.",
                  },
                  {
                    main: (
                      <>
                        <strong>Deploy safety:</strong> slow-start new pods + draining old ones prevents p95 spikes during rolling deploys.
                      </>
                    ),
                    detail:
                      "Readiness checks ensure a pod is actually ready (warm caches, loaded models) before taking traffic.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* D */}
          <Section id="nat-vpc-egress" title="D) NAT, VPC subnets, private networking, and egress patterns">
            <Say>
              “I’ll place workloads in private subnets, expose ingress via load balancers, and control outbound egress via
              NAT/proxies or private endpoints.”
            </Say>

            <Panel title="VPC & subnets — the basic mental model" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Public subnets:</strong> internet gateway route; host public LBs/NAT.
                      </>
                    ),
                    detail:
                      "Keep application services in private subnets; only controlled entry points are public.",
                  },
                  {
                    main: (
                      <>
                        <strong>Private subnets:</strong> no public inbound; inbound via LBs; outbound via NAT.
                      </>
                    ),
                    detail:
                      "Databases and services belong here in most designs.",
                  },
                  {
                    main: (
                      <>
                        <strong>Security:</strong> security groups (stateful) + NACLs (stateless) + least-privilege egress.
                      </>
                    ),
                    detail:
                      "Treat egress as a security boundary; monitor and restrict destinations.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Egress patterns — avoid ‘public internet everywhere’" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Internet egress:</strong> NAT gateway + outbound proxy; allow-list partner CIDRs/domains.
                      </>
                    ),
                    detail:
                      "Watch NAT connection/port metrics; reduce connection churn with keep-alive and pooling.",
                  },
                  {
                    main: (
                      <>
                        <strong>Private access:</strong> VPC endpoints / PrivateLink to reach managed services without public IPs.
                      </>
                    ),
                    detail:
                      "Reduces egress cost and avoids IP allowlist drift for SaaS dependencies.",
                  },
                  {
                    main: (
                      <>
                        <strong>Hybrid:</strong> VPN/DirectConnect; avoid CIDR overlap; route via transit gateway.
                      </>
                    ),
                    detail:
                      "CIDR planning early prevents painful renumbering later.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Facebook-like Home Feed — private networking & egress" tag="Example">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Private services:</strong> FeedReadSvc/PostWriteSvc in private subnets; only ALB/Ingress is public.
                      </>
                    ),
                    detail:
                      "This limits blast radius and reduces attack surface while keeping the user entry point controlled.",
                  },
                  {
                    main: (
                      <>
                        <strong>Outbound:</strong> services call 3rd-party dependencies through NAT/proxy with allow-listed egress (and strong observability).
                      </>
                    ),
                    detail:
                      "If NAT saturates (connections/ports), shard NAT, use private endpoints where possible, and reduce connection churn.",
                  },
                  {
                    main: (
                      <>
                        <strong>Media:</strong> object store origins are accessed over provider backbone; CDN pulls from shield POP/origin with tight rules.
                      </>
                    ),
                    detail:
                      "This is how you keep media delivery reliable without exposing storage directly to the internet.",
                  },
                ]}
              />
            </Panel>
          </Section>

          {/* E */}
          <Section id="end-to-end" title="E) End-to-end: say it like this in an interview (Home Feed)">
            <Say>
              For a Facebook-like Home Feed: users resolve the domain via DNS (TTL chosen for stability with the option to
              steer during incidents). DNS points to an Anycast CDN/WAF where we terminate TLS, serve cached media/static
              assets, and forward personalized API requests to an L7 load balancer/Ingress. L7 does host/path routing,
              readiness-based health checks, slow-start, and outlier ejection. Services run in private subnets and use
              mTLS service-to-service. Outbound internet access is controlled via NAT/proxy with allow-listed egress, and
              we prefer private endpoints for managed services. We enable HTTP/2 and HTTP/3 at the edge for multiplexing
              and mobile performance.
            </Say>

            <Panel title="A crisp 20-second version (Home Feed)" tag="Example">
              <MonoBlock>{`“Home Feed: DNS → Anycast CDN/WAF (TLS) → ALB/Ingress (L7 routing + health) → Feed services in private subnets (mTLS). CDN serves media with signed URLs; outbound through NAT/proxy with allow-listed egress; HTTP/2/3 at the edge.”`}</MonoBlock>
            </Panel>
          </Section>

          {/* F */}
          <Section id="pitfalls" title="F) Common pitfalls & quick fixes (Home Feed context)">
            <Say>“I’ll call out the most common networking mistakes and what I’d do first to fix them.”</Say>

            <Panel title="Pitfalls checklist" tag="Template">
              <BulletList
                items={[
                  {
                    main: (
                      <>
                        <strong>Wrong cache key / missing Vary:</strong> data leaks or poor hit ratio.
                      </>
                    ),
                    detail:
                      "For personalized feeds, be explicit: you usually don’t CDN-cache responses unless you design a private cache key (and accept complexity).",
                  },
                  {
                    main: (
                      <>
                        <strong>Timeouts too long:</strong> retry storms and tail latency blowups.
                      </>
                    ),
                    detail:
                      "Layer timeouts per hop and keep retry budget within SLO. Retry only idempotent reads; use idempotency keys for writes.",
                  },
                  {
                    main: (
                      <>
                        <strong>Sticky by client IP:</strong> breaks behind NAT/mobile and causes hot backends.
                      </>
                    ),
                    detail:
                      "Use cookies or consistent hashing on session/userId if stickiness is needed for cache locality.",
                  },
                  {
                    main: (
                      <>
                        <strong>NAT saturation:</strong> port exhaustion / connection tracking bottlenecks.
                      </>
                    ),
                    detail:
                      "Monitor NAT metrics; shard egress and reduce churn; prefer private endpoints for cloud services.",
                  },
                  {
                    main: (
                      <>
                        <strong>mTLS rotation pain:</strong> manual cert updates cause outages.
                      </>
                    ),
                    detail:
                      "Automate with mesh/SPIFFE. Short-lived certs and continuous rotation.",
                  },
                ]}
              />
            </Panel>

            <Panel title="Home Feed: quick fix priorities" tag="Example">
              <MonoBlock>{`1) Check CDN hit ratio for media + cache key correctness (Vary, signed URLs)
2) Validate L7 health checks, slow-start, draining (deploy spikes hurt feed p95)
3) Audit timeouts/retries budgets (avoid retries amplifying peak)
4) Inspect NAT metrics/flow logs for outbound bottlenecks
5) Verify mTLS rotation automation and failure modes`}</MonoBlock>
            </Panel>
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
